import type {
  InfraImpact,
  SignalCategoryType,
} from '@terra/shared-schemas-orchestrator';
import { ACL_ENGINE_EDGE_TYPES } from './internal-network-acl-engine-edges';
import type { AllowedRemoteExecutionCommand } from './internal-network-c2.constants';
import type {
  InternalNetworkCredentialPrivilegeTier,
  InternalNetworkCredentialProvenance,
} from './internal-network-attack-path.schema';

export type InternalNetworkWeaknessCredentialYield = {
  label: string;
  provenance: InternalNetworkCredentialProvenance;
  privilegeTier: InternalNetworkCredentialPrivilegeTier;
};

export type InternalNetworkWeaknessCatalogEntry = {
  weaknessId: string;
  shortName: string;
  explanation: string;
  mitreTechniqueId: string | null;
  category: SignalCategoryType | null;
  commands: readonly AllowedRemoteExecutionCommand[];
  edgeTypes: readonly string[];
  credentialYield: InternalNetworkWeaknessCredentialYield | null;
  infraImpact: InfraImpact | null;
};

export const INTERNAL_NETWORK_WEAKNESS_CATALOG: readonly InternalNetworkWeaknessCatalogEntry[] =
  [
    {
      weaknessId: 'TN-AD-0001',
      shortName: 'Kerberoasting',
      explanation:
        'Service account tickets were requested and are crackable offline because the account uses a weak password.',
      mitreTechniqueId: 'T1558.003',
      category: 'KERBEROASTING',
      commands: ['kerberoast_request'],
      edgeTypes: ['CanKerberoast'],
      credentialYield: {
        label: 'TGS Hash',
        provenance: 'DISCOVERED',
        privilegeTier: 'SERVICE_ACCOUNT',
      },
      infraImpact: 'CREDENTIAL_EXPOSURE',
    },
    {
      weaknessId: 'TN-AD-0002',
      shortName: 'AS-REP Roasting',
      explanation:
        'Accounts without Kerberos pre-authentication returned AS-REP material that can be cracked offline.',
      mitreTechniqueId: 'T1558.004',
      category: 'AS_REP_ROASTING',
      commands: ['asrep_roast'],
      edgeTypes: ['DontReqPreAuth'],
      credentialYield: {
        label: 'AS-REP Hash',
        provenance: 'DISCOVERED',
        privilegeTier: 'DOMAIN_USER',
      },
      infraImpact: 'CREDENTIAL_EXPOSURE',
    },
    {
      weaknessId: 'TN-AD-0003',
      shortName: 'DCSync',
      explanation:
        'Replication rights allowed dumping domain credential hashes directly from a domain controller.',
      mitreTechniqueId: 'T1003.006',
      category: 'DCSYNC',
      commands: ['dcsync'],
      edgeTypes: ['DCSync', 'GetChanges', 'GetChangesAll'],
      credentialYield: {
        label: 'NTLM Hash',
        provenance: 'DISCOVERED',
        privilegeTier: 'DOMAIN_ADMIN',
      },
      infraImpact: 'DOMAIN_COMPROMISE',
    },
    {
      weaknessId: 'TN-AD-0004',
      shortName: 'SMB Relay',
      explanation:
        'SMB signing was not enforced, so coerced authentication was relayed to another host to gain access.',
      mitreTechniqueId: 'T1557.001',
      category: 'SMB_RELAY',
      commands: ['ntlm_coerce'],
      edgeTypes: ['CoerceToTGT'],
      credentialYield: {
        label: 'Relayed Session',
        provenance: 'RELAYED',
        privilegeTier: 'LOCAL_ADMIN',
      },
      infraImpact: 'HOST_COMPROMISE',
    },
    {
      weaknessId: 'TN-AD-0005',
      shortName: 'Pass-the-Hash',
      explanation:
        'A captured NTLM hash was reused to authenticate to another host without cracking the password.',
      mitreTechniqueId: 'T1550.002',
      category: null,
      commands: ['pass_the_hash'],
      edgeTypes: ['AdminTo'],
      credentialYield: {
        label: 'Local Admin',
        provenance: 'COMPROMISED',
        privilegeTier: 'LOCAL_ADMIN',
      },
      infraImpact: 'HOST_COMPROMISE',
    },
    {
      weaknessId: 'TN-AD-0006',
      shortName: 'ADCS Abuse',
      explanation:
        'A vulnerable certificate template allowed requesting a certificate that impersonates a privileged account.',
      mitreTechniqueId: 'T1649',
      category: 'ADCS_ABUSE',
      commands: ['adcs_probe', 'certipy'],
      edgeTypes: ['ADCSESC1', 'ADCSESC4', 'Enroll'],
      credentialYield: {
        label: 'Certificate',
        provenance: 'DISCOVERED',
        privilegeTier: 'DOMAIN_ADMIN',
      },
      infraImpact: 'PRIVILEGE_ESCALATION',
    },
    {
      weaknessId: 'TN-AD-0007',
      shortName: 'Unconstrained Delegation',
      explanation:
        'A host configured for unconstrained delegation captured a privileged TGT for reuse.',
      mitreTechniqueId: 'T1187',
      category: 'UNCONSTRAINED_DELEGATION',
      commands: ['delegation_abuse'],
      edgeTypes: ['AllowedToDelegate', 'HasSIDHistory'],
      credentialYield: {
        label: 'TGT',
        provenance: 'DISCOVERED',
        privilegeTier: 'DOMAIN_ADMIN',
      },
      infraImpact: 'PRIVILEGE_ESCALATION',
    },
    {
      weaknessId: 'TN-AD-0008',
      shortName: 'Constrained Delegation',
      explanation:
        'Constrained delegation configuration allowed requesting a service ticket as another user.',
      mitreTechniqueId: 'T1558.003',
      category: 'CONSTRAINED_DELEGATION',
      commands: ['delegation_abuse'],
      edgeTypes: ['AllowedToDelegate'],
      credentialYield: {
        label: 'Service Ticket',
        provenance: 'DISCOVERED',
        privilegeTier: 'SERVICE_ACCOUNT',
      },
      infraImpact: 'PRIVILEGE_ESCALATION',
    },
    {
      weaknessId: 'TN-AD-0009',
      shortName: 'Resource-Based Delegation',
      explanation:
        'Write access to a computer object enabled a resource-based constrained delegation takeover.',
      mitreTechniqueId: 'T1558.003',
      category: 'RESOURCE_BASED_DELEGATION',
      commands: ['delegation_abuse'],
      edgeTypes: ['AllowedToAct', 'AddAllowedToAct'],
      credentialYield: {
        label: 'Local Admin',
        provenance: 'COMPROMISED',
        privilegeTier: 'LOCAL_ADMIN',
      },
      infraImpact: 'PRIVILEGE_ESCALATION',
    },
    {
      weaknessId: 'TN-AD-0010',
      shortName: 'ACL Abuse',
      explanation:
        'A dangerous access-control entry allowed modifying a privileged object to gain control.',
      mitreTechniqueId: 'T1222',
      category: 'ACL_ABUSE',
      commands: ['acl_probe', 'acl_abuse'],
      edgeTypes: ACL_ENGINE_EDGE_TYPES,
      credentialYield: null,
      infraImpact: 'PRIVILEGE_ESCALATION',
    },
    {
      weaknessId: 'TN-AD-0011',
      shortName: 'GPO Abuse',
      explanation:
        'Write access to a Group Policy Object allowed pushing code to every linked host.',
      mitreTechniqueId: 'T1484.001',
      category: 'GPO_ABUSE',
      commands: ['acl_probe', 'acl_abuse'],
      edgeTypes: ['GPLink', 'WriteGPLink'],
      credentialYield: null,
      infraImpact: 'DOMAIN_COMPROMISE',
    },
    {
      weaknessId: 'TN-AD-0012',
      shortName: 'LAPS Read',
      explanation:
        'Read access to LAPS attributes exposed the local administrator password of a managed host.',
      mitreTechniqueId: 'T1555',
      category: 'IDENTITY_PRIVILEGE_ABUSE',
      commands: ['laps_read'],
      edgeTypes: ['ReadLAPSPassword'],
      credentialYield: {
        label: 'Local Admin',
        provenance: 'DISCOVERED',
        privilegeTier: 'LOCAL_ADMIN',
      },
      infraImpact: 'HOST_COMPROMISE',
    },
    {
      weaknessId: 'TN-AD-0013',
      shortName: 'gMSA Read',
      explanation:
        'Read access to a group managed service account exposed its password blob.',
      mitreTechniqueId: 'T1555',
      category: 'IDENTITY_PRIVILEGE_ABUSE',
      commands: ['gmsa_read'],
      edgeTypes: ['ReadGMSAPassword'],
      credentialYield: {
        label: 'gMSA Password',
        provenance: 'DISCOVERED',
        privilegeTier: 'SERVICE_ACCOUNT',
      },
      infraImpact: 'CREDENTIAL_EXPOSURE',
    },
    {
      weaknessId: 'TN-AD-0014',
      shortName: 'Local Privilege Escalation',
      explanation:
        'A local misconfiguration allowed escalating from a standard user to local administrator.',
      mitreTechniqueId: 'T1068',
      category: 'LOCAL_PRIVILEGE_ESCALATION',
      commands: ['psremote_probe'],
      edgeTypes: [],
      credentialYield: {
        label: 'Local Admin',
        provenance: 'COMPROMISED',
        privilegeTier: 'LOCAL_ADMIN',
      },
      infraImpact: 'PRIVILEGE_ESCALATION',
    },
    {
      weaknessId: 'TN-AD-0015',
      shortName: 'Offline Hash Crack',
      explanation:
        'Captured hashes were cracked offline to recover the cleartext password.',
      mitreTechniqueId: 'T1110.002',
      category: null,
      commands: ['hash_crack_offline'],
      edgeTypes: [],
      credentialYield: {
        label: 'Cleartext Password',
        provenance: 'CRACKED',
        privilegeTier: 'DOMAIN_USER',
      },
      infraImpact: 'CREDENTIAL_EXPOSURE',
    },
    {
      weaknessId: 'TN-AD-0016',
      shortName: 'Lateral Movement',
      explanation:
        'Reused credentials granted administrative access to move laterally to another host.',
      mitreTechniqueId: 'T1021',
      category: null,
      commands: ['lateral_move_probe', 'psremote_probe'],
      edgeTypes: ['AdminTo', 'CanRDP', 'CanPSRemote', 'ExecuteDCOM'],
      credentialYield: null,
      infraImpact: 'LATERAL_MOVEMENT',
    },
  ];

const WEAKNESS_BY_COMMAND = new Map<
  string,
  InternalNetworkWeaknessCatalogEntry
>();
const WEAKNESS_BY_EDGE_TYPE = new Map<
  string,
  InternalNetworkWeaknessCatalogEntry
>();
const WEAKNESS_BY_CATEGORY = new Map<
  string,
  InternalNetworkWeaknessCatalogEntry
>();
const WEAKNESS_BY_ID = new Map<string, InternalNetworkWeaknessCatalogEntry>();

for (const entry of INTERNAL_NETWORK_WEAKNESS_CATALOG) {
  WEAKNESS_BY_ID.set(entry.weaknessId, entry);
  if (entry.category) {
    WEAKNESS_BY_CATEGORY.set(entry.category, entry);
  }

  for (const command of entry.commands) {
    if (!WEAKNESS_BY_COMMAND.has(command)) {
      WEAKNESS_BY_COMMAND.set(command, entry);
    }
  }

  for (const edgeType of entry.edgeTypes) {
    if (!WEAKNESS_BY_EDGE_TYPE.has(edgeType.toLowerCase())) {
      WEAKNESS_BY_EDGE_TYPE.set(edgeType.toLowerCase(), entry);
    }
  }
}

export const resolveWeaknessCatalogEntry = ({
  category,
  command,
  edgeType,
  technique,
}: {
  category?: string | null;
  command?: string | null;
  edgeType?: string | null;
  technique?: string | null;
}): InternalNetworkWeaknessCatalogEntry | null => {
  if (category && WEAKNESS_BY_CATEGORY.has(category)) {
    return WEAKNESS_BY_CATEGORY.get(category) ?? null;
  }

  const commandKey = command ?? technique;

  if (commandKey && WEAKNESS_BY_COMMAND.has(commandKey)) {
    return WEAKNESS_BY_COMMAND.get(commandKey) ?? null;
  }

  if (edgeType && WEAKNESS_BY_EDGE_TYPE.has(edgeType.toLowerCase())) {
    return WEAKNESS_BY_EDGE_TYPE.get(edgeType.toLowerCase()) ?? null;
  }

  return null;
};

export const getWeaknessCatalogEntryById = (
  weaknessId: string,
): InternalNetworkWeaknessCatalogEntry | null =>
  WEAKNESS_BY_ID.get(weaknessId) ?? null;
